import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import Modal from 'components/molecules/Modal';
import Button from 'atoms/Button';
import { useDeleteAccount } from './hooks';

interface DeleteAccountProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
}

const StyledModal = styled(Modal)`
  ${({ theme: { mq } }) => css`
    && main {
      background-color: transparent;
      padding: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      ${mq.md.down} {
        padding: 0;
      }
    }
  `}
`;

const Container = styled.div`
  ${({ theme: { colors, mq } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    max-width: 90%;
    width: 36rem;
    margin: 0 auto;
    background-color: ${colors.neutral.white};
    border-radius: 2rem;
    text-align: center;

    ${mq.md.up} {
      padding: 4rem;
    }
  `}
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
`;

const WarningEmoji = styled.span`
  font-size: 4rem;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  ${({ theme: { colors, tp } }) => css`
    ${tp.h1};
    color: ${colors.primary.dark};
    margin: 0;
    font-weight: 600;
    line-height: 1.3;
  `}
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 4rem;
`;

const Message = styled.p`
  ${({ theme: { colors, tp } }) => css`
    ${tp.p1};
    color: ${colors.primary.dark};
    margin: 0;
    line-height: 1.5;
  `}
`;

const WarningMessage = styled.p`
  ${({ theme: { colors, tp } }) => css`
    ${tp.h3};
    color: ${colors.primary.dark};
    margin: 0;
    font-weight: 700;
  `}
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 20rem;
`;

const DeleteButton = styled(Button)`
  ${({ theme: { colors, utils, tp } }) => css`
    ${tp.h3};
    background-color: ${colors.error.main};
    border-color: ${colors.error.main};
    color: ${colors.neutral.white};
    width: 100%;
    padding: 1.25rem 2rem;
    border-radius: 5rem;
    font-weight: 600;
    
    &:hover {
      background-color: ${utils.alphaHex(colors.error.main, 90)};
      border-color: ${utils.alphaHex(colors.error.main, 90)};
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `}
`;

const CancelButton = styled(Button)`
  ${({ theme: { colors, tp } }) => css`
    ${tp.h3};
    width: 100%;
    padding: 1.25rem 2rem;
    border-radius: 5rem;
    font-weight: 600;
    color: ${colors.primary.light};
    border-color: ${colors.primary.light};
  `}
`;

const ErrorMessage = styled.div`
  ${({ theme: { colors, tp } }) => css`
    ${tp.p2};
    color: ${colors.error.main};
    text-align: center;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background-color: ${colors.neutral.white};
    border: 1px solid ${colors.error.main};
    border-radius: 0.5rem;
  `}
`;

const DeleteAccount: React.FC<DeleteAccountProps> = ({ 
  isOpen, 
  onClose, 
  onDeleteSuccess
}) => {
  const { deleteAccount, isDeleting, error, clearError } = useDeleteAccount();

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      clearError();
      onClose();
    }
  };

  const handleConfirm = async () => {
    const success = await deleteAccount();
    if (success) {
      onDeleteSuccess();
    }
  };

  // Clear error when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearError();
    }
  }, [isOpen, clearError]);

  return (
    <StyledModal isOpen={isOpen} onClick={handleBackdropClick}>
      <Container>
        <HeaderSection>
          <WarningEmoji>⚠️</WarningEmoji>
          <Title>Are you sure you want to delete your account?</Title>
        </HeaderSection>
        
        <ContentSection>
          <Message>
            You will lose all your progress. This process can't be undone.
          </Message>
          <Message>
            It can take up to 72 hours for your account to be deleted.
          </Message>
          <WarningMessage>
            Please cancel your subscription before deleting your account.
          </WarningMessage>
        </ContentSection>
        
        {error && (
          <ErrorMessage>{error}</ErrorMessage>
        )}
        
        <ButtonSection>
          <DeleteButton 
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Forever'}
          </DeleteButton>
          <CancelButton 
            onClick={handleClose}
            disabled={isDeleting}
            variant="outlined"
          >
            Go Back
          </CancelButton>
        </ButtonSection>
      </Container>
    </StyledModal>
  );
};

export default DeleteAccount;